import React, {useId, useMemo} from 'react'
import {Box, Flex} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import Slider from '../../components/cms/slider'
import ModuleContainer from '../../components/cms/module-container'
import Teaser from '../../components/cms/teaser'
import Heading from '../../components/cms/heading'
import Copy from '../../components/cms/copy'
import Fade from '../../components/cms/fade'
import {hasValidString, parseAspectRatio} from '../../utils/utils'
import CTA from '../../components/cms/cta'
import {useSlotRecipe} from '@chakra-ui/react3'
import {carouselRecipe} from './index.recipe'
import {useIntl} from 'react-intl'
import {MODULE_TYPE_LABELS, MODULE_TYPES} from '../constants'

const Carousel = React.forwardRef(function Carousel(
    {
        moduleId,
        moduleType,
        pageId,
        headingLevel,
        alignment,
        aspectRatio,
        heading,
        copy,
        tiles,
        anchor,
        background,
        spacing,
        cta
    },
    ref
) {
    const {formatMessage} = useIntl()
    const recipe = useSlotRecipe({recipe: carouselRecipe})
    const styles = useMemo(() => recipe({alignment}), [recipe, alignment])

    const sliderId = useId()
    const ariaLabel = useMemo(
        () =>
            [
                hasValidString(heading.subline) && `${heading.subline} -`,
                heading.headline1,
                heading.headline2
            ]
                .filter(hasValidString)
                .join(' '),
        [heading.subline, heading.headline1, heading.headline2]
    )

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            anchor={anchor}
            background={background}
            spacing={spacing}
            ref={ref}
            {...styles.moduleContainer}
        >
            <Flex {...styles.headerContainer}>
                <Flex {...styles.headingCopyContainer}>
                    <Fade>
                        <Heading
                            {...heading}
                            headingLevel={headingLevel}
                            alignment={alignment}
                            type={formatMessage(MODULE_TYPE_LABELS[MODULE_TYPES.CAROUSEL])}
                        />
                    </Fade>
                    {hasValidString(copy?.text) && (
                        <Fade>
                            <Copy {...copy} alignment={alignment} />
                        </Fade>
                    )}
                </Flex>
                {cta?.label && (
                    <Box {...styles.ctaContainer}>
                        <CTA
                            {...cta}
                            variant={cta.variant === 'secondary' ? 'secondaryGhost' : cta.variant}
                            trackingContext={{moduleName: moduleType, eventLabel: 'cta'}}
                        />
                    </Box>
                )}
            </Flex>

            <Box {...styles.sliderContainer}>
                <Slider sliderLabel={ariaLabel} sliderId={sliderId} variant="carousel">
                    {tiles.map((item, index) => (
                        <Box key={index}>
                            <Teaser
                                teaserBackground={false}
                                headline={item.headline}
                                headlineId={sliderId + '-headline-' + index}
                                hideHeadline={item.hideHeadline}
                                asset={item.asset}
                                assetFlip={item.assetFlip}
                                aspectRatio={parseAspectRatio(aspectRatio)}
                                copy={item.copy}
                                cta={item.cta}
                                widths={{base: '100vw', s: 430, m: 480, l: 640}}
                                textSpacing="carousel"
                                trackingContext={{moduleName: moduleType, eventLabel: 'cta'}}
                                headingLevel={headingLevel}
                                flipButtonVariant="mediaFilled"
                            />
                        </Box>
                    ))}
                </Slider>
            </Box>
        </ModuleContainer>
    )
})

if (process.env.NODE_ENV !== 'production') {
    Carousel.displayName = 'Carousel'
}

Carousel.propTypes = {
    headingLevel: PropTypes.number,
    aspectRatio: PropTypes.string,
    heading: PropTypes.object,
    hideHeadline: PropTypes.bool,
    alignment: PropTypes.oneOf(['left', 'center']),
    copy: PropTypes.object,
    tiles: PropTypes.array,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    anchor: PropTypes.string,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    spacing: PropTypes.oneOf(['default', 'reduced', 'min', 'min-top', 'min-bottom']),
    cta: PropTypes.object
}

export default Carousel
